import { SPEAKING_CONTENT_TYPES, SPEAKING_MAX_SECONDS } from '@shared/types/speaking'

/**
 * Microphone recording.
 *
 * Kept out of React because MediaRecorder is imperative and stateful, and
 * mixing it into a component's lifecycle is how you end up leaving a
 * microphone open — the failure the browser shows the user as a permanent red
 * dot in the tab strip.
 */

export type RecorderError =
  | 'permission-denied'
  | 'no-microphone'
  | 'unsupported'
  | 'failed'

export class RecorderFailure extends Error {
  constructor(readonly reason: RecorderError) {
    super(reason)
    this.name = 'RecorderFailure'
  }
}

/**
 * Picks a container both the browser can produce and the server accepts.
 *
 * iOS Safari records `audio/mp4` and cannot do webm; Chrome and Firefox do the
 * reverse. Asking the browser rather than sniffing the user agent means this
 * keeps working when either of them changes.
 */
export function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/mpeg']
  for (const candidate of candidates) {
    if (MediaRecorder.isTypeSupported(candidate)) {
      const base = candidate.split(';')[0] as string
      if ((SPEAKING_CONTENT_TYPES as readonly string[]).includes(base)) return candidate
    }
  }
  return undefined
}

export function isRecordingSupported(): boolean {
  return (
    typeof MediaRecorder !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getUserMedia &&
    pickMimeType() !== undefined
  )
}

export interface Recording {
  blob: Blob
  contentType: string
  seconds: number
}

export class Recorder {
  private recorder: MediaRecorder | null = null
  private stream: MediaStream | null = null
  private chunks: Blob[] = []
  private startedAt = 0
  private stopTimer: ReturnType<typeof setTimeout> | null = null

  /** Live input level, 0..1, for the meter. Null when not recording. */
  private analyser: AnalyserNode | null = null
  private audioContext: AudioContext | null = null

  async start(onLevel?: (level: number) => void): Promise<void> {
    if (!isRecordingSupported()) throw new RecorderFailure('unsupported')

    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      })
    } catch (error) {
      const name = (error as { name?: string }).name
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        throw new RecorderFailure('permission-denied')
      }
      if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        throw new RecorderFailure('no-microphone')
      }
      throw new RecorderFailure('failed')
    }

    this.stream = stream
    this.chunks = []
    const mimeType = pickMimeType()
    this.recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
    this.recorder.ondataavailable = (event) => {
      if (event.data.size > 0) this.chunks.push(event.data)
    }
    this.recorder.start()
    this.startedAt = Date.now()

    // A hard stop so a forgotten recording cannot run until the upload limit
    // rejects it — the phone would just sit there with the mic open.
    this.stopTimer = setTimeout(() => {
      if (this.recorder?.state === 'recording') this.recorder.stop()
    }, SPEAKING_MAX_SECONDS * 1000)

    if (onLevel) this.startLevelMeter(stream, onLevel)
  }

  private startLevelMeter(stream: MediaStream, onLevel: (level: number) => void): void {
    try {
      const context = new AudioContext()
      const source = context.createMediaStreamSource(stream)
      const analyser = context.createAnalyser()
      analyser.fftSize = 256
      source.connect(analyser)
      this.audioContext = context
      this.analyser = analyser

      const data = new Uint8Array(analyser.frequencyBinCount)
      const tick = () => {
        if (!this.analyser) return
        this.analyser.getByteTimeDomainData(data)
        // Deviation from the 128 midpoint is the signal amplitude.
        let peak = 0
        for (const value of data) peak = Math.max(peak, Math.abs(value - 128))
        onLevel(Math.min(1, peak / 90))
        requestAnimationFrame(tick)
      }
      requestAnimationFrame(tick)
    } catch {
      // A missing AudioContext costs the meter, not the recording.
    }
  }

  async stop(): Promise<Recording> {
    const recorder = this.recorder
    if (!recorder) throw new RecorderFailure('failed')

    const blob = await new Promise<Blob>((resolve) => {
      recorder.onstop = () =>
        resolve(new Blob(this.chunks, { type: recorder.mimeType || 'audio/webm' }))
      if (recorder.state === 'recording') recorder.stop()
      else resolve(new Blob(this.chunks, { type: recorder.mimeType || 'audio/webm' }))
    })

    const seconds = (Date.now() - this.startedAt) / 1000
    this.cleanup()

    return {
      blob,
      // The server allowlist matches on the bare type; codec parameters are
      // dropped here so `audio/webm;codecs=opus` is not rejected as unknown.
      contentType: (blob.type.split(';')[0] || 'audio/webm').trim(),
      seconds,
    }
  }

  /** Safe to call at any time, including from a component unmount. */
  cleanup(): void {
    if (this.stopTimer) clearTimeout(this.stopTimer)
    this.stopTimer = null
    this.analyser = null
    void this.audioContext?.close().catch(() => undefined)
    this.audioContext = null
    // Releasing every track is what turns the recording indicator off.
    this.stream?.getTracks().forEach((track) => track.stop())
    this.stream = null
    this.recorder = null
  }
}
